import { useCallback, type Dispatch, type DragEvent, type RefObject, type SetStateAction } from "react"

import type { ReportLayoutWidgetDto, ReportLayoutWidgetType } from "../../api/reportTemplateService"
import {
  DEFAULT_FLOW_SECTION_ID,
  MIN_WIDGET_HEIGHT,
  MIN_WIDGET_WIDTH,
  WIDGET_TEMPLATES,
  clamp,
  createWidgetId,
  getWidgetFlowOrder,
  getWidgetFlowSectionId,
  getWidgetLocked,
  nextWidgetZIndex,
  parseTableColumnWidthMap,
  snapToGridValue,
  toFiniteNumber,
  type BorderSide,
} from "./editorUtils"
import type { LayoutSectionKey } from "./layoutTypes"

type UseLayoutWidgetOperationsParams = {
  canvasRef: RefObject<HTMLDivElement | null>
  layoutSection: LayoutSectionKey
  editCanvasMetrics: { width: number; height: number }
  primaryFlowSectionId: string
  manualPageCount: number
  designBodyPage: number
  designFlowSectionHeightMap: Map<string, number>
  designFlowSectionBoxes: Array<{ sectionId: string; y: number; height: number }>
  snapToGrid: boolean
  gridSize: number
  setSectionWidgets: (section: LayoutSectionKey, updater: (widgets: ReportLayoutWidgetDto[]) => ReportLayoutWidgetDto[]) => void
  moveWidgetBetweenSections: (
    widgetId: string,
    sourceSection: LayoutSectionKey,
    targetSection: LayoutSectionKey,
    options: {
      x: number
      y: number
      targetCanvasWidth: number
      targetCanvasHeight: number
      flowSectionId?: string
      manualPage?: number
    },
  ) => void
  setSelectedWidgetId: Dispatch<SetStateAction<string | null>>
  setMovingWidgetId: Dispatch<SetStateAction<string | null>>
  selectedWidget: ReportLayoutWidgetDto | null
}

export function useLayoutWidgetOperations({
  canvasRef,
  layoutSection,
  editCanvasMetrics,
  primaryFlowSectionId,
  manualPageCount,
  designBodyPage,
  designFlowSectionHeightMap,
  designFlowSectionBoxes,
  snapToGrid,
  gridSize,
  setSectionWidgets,
  moveWidgetBetweenSections,
  setSelectedWidgetId,
  setMovingWidgetId,
  selectedWidget,
}: UseLayoutWidgetOperationsParams) {
  const updateWidget = useCallback(
    (widgetId: string, patch: Partial<ReportLayoutWidgetDto>) => {
      setSectionWidgets(layoutSection, (widgets) =>
        widgets.map((widget) => {
          if (widget.id !== widgetId) return widget
          if (getWidgetLocked(widget)) return widget
          const currentX = toFiniteNumber(widget.x, 0)
          const currentY = toFiniteNumber(widget.y, 0)
          const currentWidth = toFiniteNumber(widget.width, 240)
          const currentHeight = toFiniteNumber(widget.height, 56)
          const rawWidth = toFiniteNumber(patch.width, currentWidth)
          const snappedWidth = snapToGridValue(rawWidth, gridSize, snapToGrid)
          const nextWidth = clamp(snappedWidth, MIN_WIDGET_WIDTH, editCanvasMetrics.width)
          const rawHeight = toFiniteNumber(patch.height, currentHeight)
          const snappedHeight = snapToGridValue(rawHeight, gridSize, snapToGrid)
          const nextHeight =
            layoutSection === "body"
              ? Math.max(MIN_WIDGET_HEIGHT, Math.round(snappedHeight))
              : clamp(snappedHeight, MIN_WIDGET_HEIGHT, editCanvasMetrics.height)
          const maxX = Math.max(0, editCanvasMetrics.width - nextWidth)
          const flowSectionId = getWidgetFlowSectionId(widget, primaryFlowSectionId)
          const sectionHeight = designFlowSectionHeightMap.get(flowSectionId) ?? editCanvasMetrics.height
          const maxY =
            layoutSection === "body" ? Number.POSITIVE_INFINITY : Math.max(0, sectionHeight - nextHeight)
          const rawX = toFiniteNumber(patch.x, currentX)
          const rawY = toFiniteNumber(patch.y, currentY)
          const snappedX = snapToGridValue(rawX, gridSize, snapToGrid)
          const snappedY = snapToGridValue(rawY, gridSize, snapToGrid)
          return {
            ...widget,
            ...patch,
            x: clamp(snappedX, 0, maxX),
            y: clamp(snappedY, 0, maxY),
            width: nextWidth,
            height: nextHeight,
            props:
              patch.props === undefined
                ? widget.props
                : patch.props && typeof patch.props === "object"
                  ? patch.props
                  : {},
          }
        }),
      )
    },
    [designFlowSectionHeightMap, editCanvasMetrics.height, editCanvasMetrics.width, gridSize, layoutSection, primaryFlowSectionId, setSectionWidgets, snapToGrid],
  )

  const updateWidgetProps = useCallback(
    (widgetId: string, patch: Record<string, unknown>) => {
      setSectionWidgets(layoutSection, (widgets) =>
        widgets.map((widget) => {
          if (widget.id !== widgetId) return widget
          return {
            ...widget,
            props: {
              ...(widget.props && typeof widget.props === "object" ? widget.props : {}),
              ...patch,
            },
          }
        }),
      )
    },
    [layoutSection, setSectionWidgets],
  )

  const removeWidget = useCallback(
    (widgetId: string) => {
      setSectionWidgets(layoutSection, (widgets) => widgets.filter((widget) => widget.id !== widgetId))
      setSelectedWidgetId((prev) => (prev === widgetId ? null : prev))
    },
    [layoutSection, setSectionWidgets, setSelectedWidgetId],
  )

  const duplicateWidget = useCallback(
    (widgetId: string) => {
      setSectionWidgets(layoutSection, (widgets) => {
        const sourceWidget = widgets.find((widget) => widget.id === widgetId)
        if (!sourceWidget) return widgets

        const nextWidth = clamp(
          toFiniteNumber(sourceWidget.width, 240),
          MIN_WIDGET_WIDTH,
          editCanvasMetrics.width,
        )
        const nextHeight =
          layoutSection === "body"
            ? Math.max(MIN_WIDGET_HEIGHT, Math.round(toFiniteNumber(sourceWidget.height, 56)))
            : clamp(toFiniteNumber(sourceWidget.height, 56), MIN_WIDGET_HEIGHT, editCanvasMetrics.height)
        const maxX = Math.max(0, editCanvasMetrics.width - nextWidth)
        const flowSectionId = getWidgetFlowSectionId(sourceWidget, primaryFlowSectionId)
        const sectionHeight = designFlowSectionHeightMap.get(flowSectionId) ?? editCanvasMetrics.height
        const maxY =
          layoutSection === "body" ? Number.POSITIVE_INFINITY : Math.max(0, sectionHeight - nextHeight)
        const nextFlowOrder =
          widgets.reduce((max, item, index) => Math.max(max, getWidgetFlowOrder(item, index + 1)), 0) + 1
        const nextWidget: ReportLayoutWidgetDto = {
          ...sourceWidget,
          id: createWidgetId(),
          x: clamp(
            snapToGridValue(toFiniteNumber(sourceWidget.x, 0) + 24, gridSize, snapToGrid),
            0,
            maxX,
          ),
          y: clamp(
            snapToGridValue(toFiniteNumber(sourceWidget.y, 0) + 24, gridSize, snapToGrid),
            0,
            maxY,
          ),
          zIndex: nextWidgetZIndex(widgets),
          props: {
            ...(sourceWidget.props && typeof sourceWidget.props === "object" ? sourceWidget.props : {}),
            locked: false,
            flowOrder: nextFlowOrder,
          },
        }

        setSelectedWidgetId(nextWidget.id)
        return [...widgets, nextWidget].sort((a, b) => a.zIndex - b.zIndex)
      })
    },
    [
      designFlowSectionHeightMap,
      editCanvasMetrics.height,
      editCanvasMetrics.width,
      gridSize,
      layoutSection,
      primaryFlowSectionId,
      setSectionWidgets,
      setSelectedWidgetId,
      snapToGrid,
    ],
  )

  const bringWidgetToFront = useCallback(
    (widgetId: string) => {
      setSectionWidgets(layoutSection, (widgets) =>
        widgets.map((widget) => (widget.id === widgetId ? { ...widget, zIndex: nextWidgetZIndex(widgets) } : widget)),
      )
    },
    [layoutSection, setSectionWidgets],
  )

  const createWidgetAtPosition = useCallback(
    (
      type: ReportLayoutWidgetType,
      x: number,
      y: number,
      options?: { flowSectionId?: string; manualPage?: number },
    ) => {
      const widgetTemplate = WIDGET_TEMPLATES.find((item) => item.type === type)
      if (!widgetTemplate) return

      setSectionWidgets(layoutSection, (widgets) => {
        const width = widgetTemplate.defaultWidth
        const height = widgetTemplate.defaultHeight
        const nextFlowOrder =
          widgets.reduce((max, item, index) => Math.max(max, getWidgetFlowOrder(item, index + 1)), 0) + 1
        const flowSectionId =
          layoutSection === "body" ? options?.flowSectionId || primaryFlowSectionId : DEFAULT_FLOW_SECTION_ID
        const safeManualPage =
          layoutSection === "body" ? clamp(options?.manualPage ?? designBodyPage, 1, manualPageCount) : 1
        const snappedX = clamp(
          snapToGridValue(x, gridSize, snapToGrid),
          0,
          Math.max(0, editCanvasMetrics.width - width),
        )
        const snappedYRaw =
          layoutSection === "body"
            ? Math.max(0, snapToGridValue(y, gridSize, snapToGrid))
            : clamp(
                snapToGridValue(y, gridSize, snapToGrid),
                0,
                Math.max(0, editCanvasMetrics.height - height),
              )
        const nextWidget: ReportLayoutWidgetDto = {
          id: createWidgetId(),
          type,
          x: snappedX,
          y: snappedYRaw,
          width,
          height,
          zIndex: nextWidgetZIndex(widgets),
          props: {
            ...widgetTemplate.defaultProps,
            positionMode: "absolute",
            flowOrder: nextFlowOrder,
            manualPage: safeManualPage,
            flowSectionId,
          },
        }
        setSelectedWidgetId(nextWidget.id)
        return [...widgets, nextWidget].sort((a, b) => a.zIndex - b.zIndex)
      })
    },
    [designBodyPage, editCanvasMetrics.height, editCanvasMetrics.width, gridSize, layoutSection, manualPageCount, primaryFlowSectionId, setSectionWidgets, setSelectedWidgetId, snapToGrid],
  )

  const getDropCoordinates = useCallback(
    (
      event: DragEvent,
      widgetWidth: number,
      widgetHeight: number,
      targetCanvasMetrics = editCanvasMetrics,
    ) => {
      const currentTarget = event.currentTarget as HTMLElement | null
      const canvasRect = currentTarget?.getBoundingClientRect() ?? canvasRef.current?.getBoundingClientRect()
      if (!canvasRect) return { x: 40, y: 40 }
      const relativeX = ((event.clientX - canvasRect.left) / canvasRect.width) * targetCanvasMetrics.width
      const relativeY = ((event.clientY - canvasRect.top) / canvasRect.height) * targetCanvasMetrics.height
      const rawX = clamp(relativeX - widgetWidth / 2, 0, Math.max(0, targetCanvasMetrics.width - widgetWidth))
      const rawY = clamp(relativeY - widgetHeight / 2, 0, Math.max(0, targetCanvasMetrics.height - widgetHeight))
      return {
        x: clamp(
          snapToGridValue(rawX, gridSize, snapToGrid),
          0,
          Math.max(0, targetCanvasMetrics.width - widgetWidth),
        ),
        y: clamp(
          snapToGridValue(rawY, gridSize, snapToGrid),
          0,
          Math.max(0, targetCanvasMetrics.height - widgetHeight),
        ),
      }
    },
    [canvasRef, editCanvasMetrics, gridSize, snapToGrid],
  )

  const handleCanvasDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }, [])

  const handleWidgetTemplateDragStart = useCallback(
    (type: ReportLayoutWidgetType, event: DragEvent<HTMLButtonElement>) => {
      event.dataTransfer.setData("text/report-layout", JSON.stringify({ mode: "new", type }))
      event.dataTransfer.effectAllowed = "copyMove"
    },
    [],
  )

  const handleWidgetTransferDragStart = useCallback(
    (
      sourceSection: LayoutSectionKey,
      widgetId: string,
      widgetType: ReportLayoutWidgetType,
      widgetWidth: number,
      widgetHeight: number,
      event: DragEvent<HTMLElement>,
    ) => {
      event.dataTransfer.setData(
        "text/report-layout",
        JSON.stringify({
          mode: "move",
          sourceSection,
          widgetId,
          type: widgetType,
          width: widgetWidth,
          height: widgetHeight,
        }),
      )
      event.dataTransfer.effectAllowed = "move"
    },
    [],
  )

  const handleCanvasDrop = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
      targetSection: LayoutSectionKey = layoutSection,
      targetCanvasMetrics = editCanvasMetrics,
      options?: { flowSectionId?: string; manualPage?: number },
    ) => {
      event.preventDefault()
      const raw = event.dataTransfer.getData("text/report-layout")
      if (!raw) return

      try {
        const payload = JSON.parse(raw) as {
          mode: "new" | "move"
          type?: ReportLayoutWidgetType
          widgetId?: string
          sourceSection?: LayoutSectionKey
          width?: number
          height?: number
        }
        if (payload.mode === "new" && payload.type) {
          const template = WIDGET_TEMPLATES.find((item) => item.type === payload.type)
          const width = template?.defaultWidth ?? 240
          const height = template?.defaultHeight ?? 56
          const pos = getDropCoordinates(event, width, height, targetCanvasMetrics)
          if (targetSection === "body") {
            const centerY = pos.y + height / 2
            const sectionBox =
              designFlowSectionBoxes.find((box) => centerY >= box.y && centerY <= box.y + box.height) ??
              designFlowSectionBoxes.find((box) => box.sectionId === options?.flowSectionId) ??
              designFlowSectionBoxes[0]
            const localY = sectionBox ? Math.max(0, pos.y - sectionBox.y) : pos.y
            createWidgetAtPosition(payload.type, pos.x, localY, {
              flowSectionId: options?.flowSectionId || sectionBox?.sectionId,
              manualPage: options?.manualPage ?? designBodyPage,
            })
          } else {
            createWidgetAtPosition(payload.type, pos.x, pos.y)
          }
          return
        }

        if (
          payload.mode === "move" &&
          payload.widgetId &&
          payload.sourceSection &&
          ["body", "header", "footer"].includes(payload.sourceSection)
        ) {
          const sourceSection = payload.sourceSection
          const width = Number(payload.width) || (selectedWidget?.id === payload.widgetId ? Number(selectedWidget.width) || 240 : 240)
          const height = Number(payload.height) || (selectedWidget?.id === payload.widgetId ? Number(selectedWidget.height) || 56 : 56)
          const pos = getDropCoordinates(event, width, height, targetCanvasMetrics)
          moveWidgetBetweenSections(payload.widgetId, sourceSection, targetSection, {
            x: pos.x,
            y: pos.y,
            targetCanvasWidth: targetCanvasMetrics.width,
            targetCanvasHeight: targetCanvasMetrics.height,
            flowSectionId: options?.flowSectionId,
            manualPage: options?.manualPage ?? designBodyPage,
          })
          if (sourceSection !== targetSection) {
            setMovingWidgetId(null)
          }
        }
      } catch {
        // ignore malformed drag payload
      } finally {
        setMovingWidgetId(null)
      }
    },
    [
      createWidgetAtPosition,
      designBodyPage,
      designFlowSectionBoxes,
      editCanvasMetrics,
      getDropCoordinates,
      layoutSection,
      moveWidgetBetweenSections,
      selectedWidget,
      setMovingWidgetId,
    ],
  )

  const toggleTableColumn = useCallback(
    (widgetId: string, fieldKey: string, checked: boolean) => {
      const current = String(selectedWidget?.props?.columns || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
      const next = checked ? Array.from(new Set([...current, fieldKey])) : current.filter((item) => item !== fieldKey)

      const currentWidths = parseTableColumnWidthMap(selectedWidget?.props?.columnWidths)
      if (!checked && fieldKey in currentWidths) {
        delete currentWidths[fieldKey]
      }

      updateWidgetProps(widgetId, {
        columns: next.join(","),
        columnWidths: currentWidths,
      })
    },
    [selectedWidget?.props?.columnWidths, selectedWidget?.props?.columns, updateWidgetProps],
  )

  const moveTableColumn = useCallback(
    (widgetId: string, fieldKey: string, direction: "up" | "down") => {
      const current = String(selectedWidget?.props?.columns || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
      const index = current.indexOf(fieldKey)
      if (index < 0) return
      const target = direction === "up" ? index - 1 : index + 1
      if (target < 0 || target >= current.length) return

      const next = [...current]
      const swap = next[target]
      next[target] = next[index]
      next[index] = swap
      updateWidgetProps(widgetId, { columns: next.join(",") })
    },
    [selectedWidget?.props?.columns, updateWidgetProps],
  )

  const setTableColumnWidth = useCallback(
    (widgetId: string, fieldKey: string, widthPercent: number | null) => {
      const currentWidths = parseTableColumnWidthMap(selectedWidget?.props?.columnWidths)
      if (widthPercent == null || !Number.isFinite(widthPercent) || widthPercent <= 0) {
        delete currentWidths[fieldKey]
      } else {
        currentWidths[fieldKey] = clamp(Math.round(widthPercent * 100) / 100, 1, 100)
      }
      updateWidgetProps(widgetId, { columnWidths: currentWidths })
    },
    [selectedWidget?.props?.columnWidths, updateWidgetProps],
  )

  const setWidgetBorderSide = useCallback(
    (widgetId: string, side: BorderSide, checked: boolean) => {
      const keyMap: Record<BorderSide, "borderTop" | "borderRight" | "borderBottom" | "borderLeft"> = {
        top: "borderTop",
        right: "borderRight",
        bottom: "borderBottom",
        left: "borderLeft",
      }
      updateWidgetProps(widgetId, { [keyMap[side]]: checked })
    },
    [updateWidgetProps],
  )

  const setWidgetBorderAll = useCallback(
    (widgetId: string, checked: boolean) => {
      updateWidgetProps(widgetId, {
        borderTop: checked,
        borderRight: checked,
        borderBottom: checked,
        borderLeft: checked,
      })
    },
    [updateWidgetProps],
  )

  return {
    updateWidget,
    updateWidgetProps,
    removeWidget,
    duplicateWidget,
    bringWidgetToFront,
    createWidgetAtPosition,
    handleWidgetTransferDragStart,
    handleCanvasDragOver,
    handleWidgetTemplateDragStart,
    handleCanvasDrop,
    toggleTableColumn,
    moveTableColumn,
    setTableColumnWidth,
    setWidgetBorderSide,
    setWidgetBorderAll,
  }
}
